import type { SportsbookId } from "@/lib/sportsbook-registry";
import type { DisplayPlayerProp, PlatformType } from "./utils-v2";

export const SPORTSBOOK_FIELD_GROUPS: Partial<Record<SportsbookId, (keyof DisplayPlayerProp)[]>> = {
  prizepicks: [
    "prizepicks_odds",
    "prizepicks_over_odds",
    "prizepicks_under_odds",
  ],
  underdog: [
    "underdog_odds",
    "underdog_over_odds",
    "underdog_under_odds",
    "underdog_direct_odds",
  ],
  sleeper: [
    "sleeper_odds",
    "sleeper_over_odds",
    "sleeper_under_odds",
    "sleeper_direct_odds",
  ],
  fliff: ["fliff_odds"],
  draftkings: [
    "draftkings_odds",
    "draftkings_over_odds",
    "draftkings_under_odds",
  ],
  fanduel: ["fanduel_odds", "fanduel_over_odds", "fanduel_under_odds"],
  caesars: ["caesars_odds", "caesars_over_odds", "caesars_under_odds"],
  betmgm: ["betmgm_odds", "betmgm_over_odds", "betmgm_under_odds"],
  fanatics: [
    "fanatics_odds",
    "fanatics_over_odds",
    "fanatics_under_odds",
  ],
  hardrock: [
    "hardrock_odds",
    "hardrock_over_odds",
    "hardrock_under_odds",
  ],
  betr: ["betr_odds", "betr_over_odds", "betr_under_odds"],
  novig: ["novig_odds", "novig_over_odds", "novig_under_odds"],
  prophetx: [
    "prophetx_odds",
    "prophetx_over_odds",
    "prophetx_under_odds",
  ],
  circa: [
    "circa_odds",
    "circa_over_odds",
    "circa_under_odds",
  ],
  dabble: [
    "dabble_odds",
    "dabble_over_odds",
    "dabble_under_odds",
  ],
  pinnacle: [
    "pinnacle_odds",
    "pinnacle_over_odds",
    "pinnacle_under_odds",
  ],
  parlayplay: [
    "parlayplay_over_odds",
    "parlayplay_under_odds",
    "parlayplay_odds",
  ],
  kalshi: [
    "kalshi_odds",
    "kalshi_over_odds",
    "kalshi_under_odds",
  ],
  polymarket: [
    "polymarket_odds",
  ],
  rebet: [
    "rebet_odds",
  ],
  draftkingspredictions: [
    "draftkingspredictions_odds",
    "draftkingspredictions_over_odds",
    "draftkingspredictions_under_odds",
  ],
  kambi: [
    "kambi_odds",
    "kambi_over_odds",
    "kambi_under_odds",
  ],
  betonline: [
    "betonline_odds",
    "betonline_over_odds",
    "betonline_under_odds",
  ],
  propbuilder: [
    "propbuilder_odds",
    "propbuilder_over_odds",
    "propbuilder_under_odds",
  ],
  bovada: [
    "bovada_odds",
    "bovada_over_odds",
    "bovada_under_odds",
  ],
  betrsportsbook: [
    "betrsportsbook_odds",
    "betrsportsbook_over_odds",
    "betrsportsbook_under_odds",
  ],
  shuffle: [
    "shuffle_odds",
    "shuffle_over_odds",
    "shuffle_under_odds",
  ],
  sportzino: [
    "sportzino_odds",
    "sportzino_over_odds",
    "sportzino_under_odds",
  ],
  sportsbettingag: [
    "sportsbettingag_odds",
    "sportsbettingag_over_odds",
    "sportsbettingag_under_odds",
  ],
  dkpick6: [
    "dkpick6_odds",
    "dkpick6_over_odds",
    "dkpick6_under_odds",
  ],
  tailgate: [
    "tailgate_odds",
    "tailgate_over_odds",
    "tailgate_under_odds",
  ],
  sleeperpredict: ["sleeperpredict_odds"],
  courtside: [
    "courtside_odds",
    "courtside_over_odds",
    "courtside_under_odds",
  ],
  boomsports: [
    "boomsports_odds",
    "boomsports_over_odds",
    "boomsports_under_odds",
  ],
  chalkboardpicks: [
    "chalkboardpicks_odds",
    "chalkboardpicks_over_odds",
    "chalkboardpicks_under_odds",
  ],
  chalkboardssp: [
    "chalkboardssp_odds",
    "chalkboardssp_over_odds",
    "chalkboardssp_under_odds",
  ],
  smacktok: [
    "smacktok_odds",
    "smacktok_over_odds",
    "smacktok_under_odds",
  ],
  fourcx: ["fourcx_odds", "fourcx_over_odds", "fourcx_under_odds"],
  fourcasters: ["fourcasters_odds", "fourcasters_over_odds", "fourcasters_under_odds"],
  sxbet: ["sxbet_odds", "sxbet_over_odds", "sxbet_under_odds"],
  bet105: ["bet105_odds", "bet105_over_odds", "bet105_under_odds"],
  onyxodds: ["onyxodds_odds", "onyxodds_over_odds", "onyxodds_under_odds"],
};

export function hasFieldMappingForBook(book: SportsbookId | PlatformType): boolean {
  const fields = SPORTSBOOK_FIELD_GROUPS[book];
  return Array.isArray(fields) && fields.length > 0;
}

export function getBooksWithFieldGroups(): SportsbookId[] {
  return (Object.keys(SPORTSBOOK_FIELD_GROUPS) as SportsbookId[]).filter(
    (book) => hasFieldMappingForBook(book)
  );
}

export const OPTIMIZER_ANALYTICAL_BOOKS: readonly PlatformType[] =
  getBooksWithFieldGroups();

export function orderOptimizerAnalyticalBooks(
  booksWithCurrentData: readonly PlatformType[] = [],
  analyticalBookIds: readonly PlatformType[] = OPTIMIZER_ANALYTICAL_BOOKS,
): PlatformType[] {
  const fullCatalog = new Set(analyticalBookIds);
  const availableFirst = booksWithCurrentData.filter((book) =>
    fullCatalog.has(book),
  );
  const seen = new Set(availableFirst);
  return [
    ...availableFirst,
    ...analyticalBookIds.filter((book) => !seen.has(book)),
  ];
}
